import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Link2, Share2, ImageDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getRealityScore } from '@/utils/realityScore';
import { downloadResultCard } from '@/utils/resultCard';
import { trackEvent } from '@/lib/analytics';

interface ResultsDisplayProps {
  probability: number;
  absoluteNumbers: {
    compatible: number;
    total: number;
  };
  breakdown: {
    factor: string;
    percentage: number;
  }[];
  /** Which population the result describes. Defaults to women. */
  noun?: 'women' | 'men';
  /** Shareable URL encoding the current inputs; enables the share toolbar. */
  shareUrl?: string;
}

export function ResultsDisplay({
  probability,
  absoluteNumbers,
  breakdown,
  noun = 'women',
  shareUrl,
}: ResultsDisplayProps) {
  const { toast } = useToast();
  const realityScore = getRealityScore(probability);

  const formatPercentage = (value: number) => {
    if (value < 0.01) {
      return `<0.01%`;
    }
    return `${value.toFixed(2)}%`;
  };

  const formatFraction = (probability: number) => {
    const outOf = Math.round(100 / probability);
    return `1 out of every ${outOf.toLocaleString()} ${noun}`;
  };

  const cardOptions = {
    probabilityLabel: formatPercentage(probability),
    fractionLabel: formatFraction(probability),
    compatibleLabel: `~${absoluteNumbers.compatible.toLocaleString()} ${noun} in the US`,
    realityScore,
    noun,
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Link copied', description: 'Share your result with anyone.' });
    } catch {
      toast({ title: 'Could not copy link', description: shareUrl });
    }
    trackEvent('share_clicked', { method: 'copy_link', calculator: noun });
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.share({
        title: `My dating Reality Score: ${realityScore.score}/10 (${realityScore.band})`,
        text: `Only ${formatPercentage(probability)} of US ${noun} match my standards. What's your Reality Score?`,
        url: shareUrl,
      });
      trackEvent('share_clicked', { method: 'native_share', calculator: noun });
    } catch {
      // User dismissed the share sheet.
    }
  };

  const handleDownloadCard = () => {
    downloadResultCard(cardOptions);
    trackEvent('share_clicked', { method: 'download_card', calculator: noun });
  };

  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  const StickFigureGrid = useMemo(() => {
    const totalFigures = 1000;
    const highlightedCount = Math.round((probability / 100) * totalFigures);

    const figures = [];
    for (let i = 0; i < totalFigures; i++) {
      const isHighlighted = i < highlightedCount;
      figures.push(
        <div
          key={i}
          className={`transition-colors duration-300 ${
            isHighlighted ? 'text-primary animate-pulse' : 'text-muted/30'
          }`}
          style={{ animationDelay: `${i * 2}ms` }}
        >
          <User size={12} />
        </div>
      );
    }
    return figures;
  }, [probability]);

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6" id="results">
      {/* Main Result */}
      <Card>
        <CardHeader>
          <CardTitle>Compatibility Probability</CardTitle>
          <CardDescription>
            Based on U.S. demographic data and your specified preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <div className="text-6xl font-bold text-primary">
              {formatPercentage(probability)}
            </div>
            <div className="text-xl text-muted-foreground">
              {formatFraction(probability)}
            </div>
            <div className="text-sm text-muted-foreground">
              Approximately {absoluteNumbers.compatible.toLocaleString()} {noun} in the United States
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reality Score */}
      <Card className="border-2 border-primary/30">
        <CardHeader>
          <CardTitle className="text-center">Reality Score</CardTitle>
          <CardDescription className="text-center">
            How your standards compare to the statistical reality
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-5xl font-extrabold text-primary">{realityScore.score}</span>
              <span className="text-2xl text-muted-foreground font-semibold">/ 10</span>
            </div>
            <div className="text-2xl font-bold">{realityScore.band}</div>
            <p className="text-muted-foreground">{realityScore.blurb}</p>
            {/* Score scale */}
            <div className="flex gap-1 max-w-md mx-auto pt-2" aria-hidden="true">
              {Array.from({ length: 10 }, (_, i) => (
                <div
                  key={i}
                  className={`h-2 flex-1 rounded-full ${
                    i < realityScore.score ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
            <div className="flex justify-between max-w-md mx-auto text-xs text-muted-foreground">
              <span>Grounded</span>
              <span>Unicorn Hunter</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Share toolbar */}
      {shareUrl && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="text-sm font-medium mr-1">Share your result:</span>
              <Button variant="outline" size="sm" onClick={handleCopyLink}>
                <Link2 className="w-4 h-4 mr-2" />
                Copy link
              </Button>
              {canNativeShare && (
                <Button variant="outline" size="sm" onClick={handleNativeShare}>
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleDownloadCard}>
                <ImageDown className="w-4 h-4 mr-2" />
                Download result card
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visual Representation */}
      <Card className="bg-gradient-to-br from-background to-muted/50 border-2">
        <CardHeader>
          <CardTitle className="text-center">Reality Check</CardTitle>
          <CardDescription className="text-center">
            Each figure represents 0.1% of {noun} - see how rare your match really is
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Stick Figure Grid */}
            <div className="relative p-6 bg-card/50 rounded-lg border">
              <div
                className="grid gap-1 justify-center"
                style={{
                  gridTemplateColumns: 'repeat(50, minmax(0, 1fr))',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}
              >
                {StickFigureGrid}
              </div>
              <div className="absolute top-2 right-2 text-xs text-muted-foreground">
                1,000 {noun}
              </div>
            </div>

            <div className="text-center space-y-4">
              <div className="text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-primary animate-pulse"></div>
                  Compatible matches
                </span>
                <span className="mx-4">•</span>
                <span className="inline-flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-muted/30"></div>
                  General population
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Factor Breakdown</CardTitle>
          <CardDescription>
            How each preference affects the overall probability
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {breakdown.map((item, index) => (
              <div key={index} className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                <span className="font-medium">{item.factor}</span>
                <span className="text-sm text-muted-foreground">
                  {formatPercentage(item.percentage)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
